import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import EnvironmentBadge from "./components/EnvironmentBadge";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteFallback from "./components/RouteFallback";
import ProtectedRoute from "./components/ProtectedRoute";
import DealerRoute from "./components/DealerRoute";
import AdminRoute from "./components/AdminRoute";

const HomePage = lazy(() => import("./pages/carva-home"));
const ListingsPage = lazy(() => import("./pages/carva-listings"));
const SellPage = lazy(() => import("./pages/SellPage"));
const CarDetailPage = lazy(() => import("./pages/CarDetailPage"));
const DealersPage = lazy(() => import("./pages/DealersPage"));
const DealerProfilePage = lazy(() => import("./pages/DealerProfilePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/carva-signup"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const LogoutPage = lazy(() => import("./pages/LogoutPage"));
const AccountProfilePage = lazy(() => import("./pages/AccountProfilePage"));
const AccountListingsPage = lazy(() => import("./pages/AccountListingsPage"));
const AccountSavedPage = lazy(() => import("./pages/AccountSavedPage"));
const DealerDashboardPage = lazy(() => import("./pages/DealerDashboardPage"));
const DashboardListingsPage = lazy(() => import("./pages/DashboardListingsPage"));
const DashboardAnalyticsPage = lazy(() => import("./pages/DashboardAnalyticsPage"));
const DashboardBillingPage = lazy(() => import("./pages/DashboardBillingPage"));
const DashboardProfilePage = lazy(() => import("./pages/DashboardProfilePage"));

const AdminDashboard = lazy(() =>
  import("./pages/AdminPages").then((m) => ({ default: m.AdminDashboard }))
);
const AdminListings = lazy(() =>
  import("./pages/AdminPages").then((m) => ({ default: m.AdminListings }))
);
const AdminUsers = lazy(() =>
  import("./pages/AdminPages").then((m) => ({ default: m.AdminUsers }))
);
const AdminDealers = lazy(() =>
  import("./pages/AdminPages").then((m) => ({ default: m.AdminDealers }))
);
const AdminReports = lazy(() =>
  import("./pages/AdminPages").then((m) => ({ default: m.AdminReports }))
);
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

export default function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
      <ErrorBoundary>
      <Router>
        <EnvironmentBadge />
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/listings" element={<ListingsPage />} />
            <Route path="/car/:id" element={<CarDetailPage />} />
            <Route path="/dealers" element={<DealersPage />} />
            <Route path="/dealer/:id" element={<DealerProfilePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/logout" element={<LogoutPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/sell" element={<SellPage />} />
              <Route path="/account" element={<AccountProfilePage />} />
              <Route path="/account/listings" element={<AccountListingsPage />} />
              <Route path="/account/saved" element={<AccountSavedPage />} />
            </Route>

            <Route element={<DealerRoute />}>
              <Route path="/dashboard" element={<DealerDashboardPage />} />
              <Route path="/dashboard/listings" element={<DashboardListingsPage />} />
              <Route path="/dashboard/analytics" element={<DashboardAnalyticsPage />} />
              <Route path="/dashboard/billing" element={<DashboardBillingPage />} />
              <Route path="/dashboard/profile" element={<DashboardProfilePage />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/listings" element={<AdminListings />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/dealers" element={<AdminDealers />} />
              <Route path="/admin/reports" element={<AdminReports />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Router>
      </ErrorBoundary>
    </LanguageProvider>
    </ThemeProvider>
  );
}
